import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router";
import { EmpdContext } from '../../Context';
import { useMsal } from '@azure/msal-react';
import Button from '../../Components/Button';
import { createUseStyles } from "react-jss";
import { NavLink } from "react-router";
import ContentWrapper from "../ContentWrapper";
import useMobile from "../../Hooks/useMobile";
import { handleCallback } from "../../utils";

type link = {
  name: string;
  path: string;
};

interface Props {
  logo: string;
  links: link[];
  primaryLink: link;
  inverse?: boolean;
}

const useStyles = createUseStyles({
  rootWrapper: {
    borderBottom: "1px solid #e4e5eb",
    padding: "20px",
    backgroundColor: "#fff",
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
  root: {
    position: "relative",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  heroButton: {
    alignSelf: "flex-start",
    marginTop: "20px",
    display: "inline-block",
  },
  logo: {
    width: "165px",
    "@media (max-width: 1480px)": {
      width: "90px",
    },
  },
  navLinksWrapper: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  navLinks: {
    marginRight: "20px",
  },
  link: {
    textDecoration: "none",
    borderRadius: "5px",
    padding: "10px 20px",
    color: "#002F6C",
    fontWeight: "bold",
    fontSize: "14px",
  },
  activeLink: {
    background: "#002F6C",
    color: "#fff",
  },
  activePrimaryLink: {
    background: "#F6BB60",
  },
  mobileNavLink: {
    padding: "20px",
    "& i": {
      fontSize: "1.5rem",
      color: "#002f6c",
    },
  },
  mobileNavLinksWrapper: {
    display: "flex",
    flexDirection: "column",
    position: "fixed",
    top: 0,
    right: 0,
    left: 0,
    bottom: 0,
    background: "#fff",
    zIndex: 100,
    padding: "20px",
  },
  mobileNavLinks: {
    marginTop: "50px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    width: "100%",
  },
  mobileLink: {
    padding: "15px 20px",
    fontSize: "16px",
    marginBottom: "15px",
    textAlign: "center",
  },
  mobileNavHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  homeLink: {
    cursor: "pointer",
  },
});

const handleLoginClick = async (type: string, msalInstance: any, navigate:(value: string) => void) => {
	if( type === 'patients' ) {
		if( msalInstance.getActiveAccount()) {
            navigate('../registered')
        } else {
            msalInstance.loginRedirect({
                authority: "https://globalempd.b2clogin.com/globalempd.onmicrosoft.com/B2C_1_EMPD",
                state: `${window.location.origin}/registered`,
                scopes: ['profile','openid'],
            })
        }

	} else if( type === 'providers' ) {
		if( msalInstance.getActiveAccount()) {
			navigate('provider/dashboard')
		} else {
			msalInstance.loginRedirect({
				authority: "https://globalempd.b2clogin.com/globalempd.onmicrosoft.com/B2C_1_EMPD_PROVIDER", 
				state: `${window.location.origin}/provider/dashboard`,
				scopes: ['profile','openid'],
			})
		}
	}
}

const useMyContext = () => {
    const context = useContext(EmpdContext)
    if( !context ) {
        throw new Error("No provider found for Context")
    }
    return context
}

const handleClick = () => {
  window.open("https://winshipcancer.emory.edu/", "_blank");
};

const Navigation = ({ logo, links, primaryLink, inverse = false }: Props) => {
  const { createRedcapEntry, fetchNamespace, setAuthUser } = useMyContext()
  const { instance } = useMsal();
  const navigate = useNavigate();

  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const classes = useStyles();
  const isMobile = useMobile();
  const location = useLocation();

  useEffect(() => {
  	fetchNamespace()
  },[])

  useEffect(() => {
	if( location.hash.length > 0 ) {
	    handleCallback(instance, createRedcapEntry, setAuthUser)
	}
  }, [location])

  return (
    <div className={classes.rootWrapper}>
      <ContentWrapper>
        <div
          className={classes.root}
          style={{ flexDirection: inverse ? "row-reverse" : "row" }}
        >
          {logo && (
            <div className={classes.homeLink} onClick={handleClick}>
              <img className={classes.logo} alt="logo" src={logo} />
            </div>
          )}
          {isMobile && (
            <div
              className={classes.mobileNavLink}
              onClick={() => setMobileNavOpen(true)}
            >
              <i className="fa-solid fa-bars"></i>
            </div>
          )}
          {!isMobile && (
            <div className={classes.navLinksWrapper}>
              <div className={classes.navLinks}>
                {links.map((link) => (
                  <NavLink
                    key={link.path}
                    to={link.path}
                    className={({ isActive }) =>
                      isActive
                        ? `${classes.link} ${classes.activeLink}`
                        : classes.link
                    }
                    end
                  >
                    {link.name}
                  </NavLink>
                ))}
              </div>
              <div className={classes.navLinks}>
                <Button
                  className={`${classes.link} ${classes.activePrimaryLink}`}
                  label={`Patients`}
                  onClick={() => handleLoginClick('patients', instance, navigate)}
                />
              </div>
              <div className={classes.navLinks}>
                <Button
                  className={`${classes.link} ${classes.activePrimaryLink}`}
                  label={`Providers`}
                  onClick={() => handleLoginClick('providers', instance, navigate)}
                />
			  </div>
            </div>
          )}
          {isMobile && mobileNavOpen && (
            <div className={classes.mobileNavLinksWrapper}>
              <div
                className={classes.mobileNavHeader}
                style={{ flexDirection: inverse ? "row-reverse" : "row" }}
              >
                {logo && <img className={classes.logo} alt="logo" src={logo} />}
                <div
                  className={classes.mobileNavLink}
                  onClick={() => setMobileNavOpen(false)}
                >
                  <i className="fa-solid fa-xmark"></i>
                </div>
              </div>
              <div className={classes.mobileNavLinks}>
                {links.map((link) => (
                  <NavLink
                    to={link.path}
                    onClick={() => setMobileNavOpen(false)}
                    className={({ isActive }) =>
                      isActive
                        ? `${classes.link} ${classes.mobileLink} ${classes.activeLink}`
                        : `${classes.link} ${classes.mobileLink}`
                    }
                  >
                    {link.name}
                  </NavLink>
                ))}
              </div>
              <div className={classes.mobileNavLinks}>
                <Button
                  className={classes.heroButton}
                  label={`Patients`}
                  onClick={() => handleLoginClick('patients', instance, navigate)}
                />
              </div>
              <div className={classes.mobileNavLinks}>
                <Button
                  className={classes.heroButton}
                  label={`Providers`}
                  onClick={() => handleLoginClick('providers', instance, navigate)}
                />
			  </div>
            </div>
          )}
        </div>
      </ContentWrapper>
    </div>
  );
};

export default Navigation;
